import { useEffect, useRef } from 'react'
import { Link } from 'react-router'

import { ApiError, errorMessage } from '@/api/client'
import { useChangePlan, usePlanChangePreview } from '@/api/queries'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { formatAbsolute } from '@/lib/format'
import { intervalName, outcomeNotice, planErrorText, planName, summarisePreview } from '@/lib/plan-change'

const date = (iso: string | null): string => (iso === null ? 'the end of the period' : formatAbsolute(iso))

/**
 * Change plan or interval on a live subscription (BI7). The proration is previewed before anything
 * is charged; upgrades apply now, downgrades are scheduled for the period end and can be undone.
 */
export function ChangePlanDialog({
  plan,
  interval,
  onClose,
}: {
  plan: string
  interval: 'month' | 'year'
  onClose: () => void
}) {
  const preview = usePlanChangePreview()
  const change = useChangePlan()
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true
    preview.mutate({ plan, interval })
  }, [preview, plan, interval])

  const summary = preview.data === undefined ? null : summarisePreview(preview.data, date)
  const previewCode = preview.error instanceof ApiError ? preview.error.code : undefined
  const changeCode = change.error instanceof ApiError ? change.error.code : undefined
  const result = change.data
  // bug-223: the plan in force chosen again while a downgrade is scheduled.
  const keeping = preview.data?.direction === 'keep'

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !change.isPending) onClose()
      }}
    >
      <DialogContent data-testid="change-plan-dialog">
        <DialogHeader>
          <DialogTitle>
            {keeping ? 'Keep' : 'Change to'} {planName(plan)} ({intervalName(interval)})
          </DialogTitle>
          <DialogDescription>Review what this costs before you confirm. Nothing changes until you do.</DialogDescription>
        </DialogHeader>

        {result !== undefined ? (
          result.outcome === 'payment_required' ? (
            <Alert variant="warning" data-testid="change-payment-required">
              <AlertTitle>Payment needed</AlertTitle>
              <AlertDescription>
                {outcomeNotice(result, date)}{' '}
                {result.invoice_id === null ? null : (
                  <Link
                    className="font-medium underline"
                    to={`/app/invoices?open=${encodeURIComponent(result.invoice_id)}`}
                    onClick={onClose}
                  >
                    Pay the invoice
                  </Link>
                )}
              </AlertDescription>
            </Alert>
          ) : (
            <Alert variant="info" data-testid="change-done">
              <AlertDescription>{outcomeNotice(result, date)}</AlertDescription>
            </Alert>
          )
        ) : preview.isPending || preview.isIdle ? (
          <Skeleton className="h-20 w-full" />
        ) : preview.isError ? (
          <Alert variant="destructive" data-testid="preview-error">
            <AlertDescription>{planErrorText(previewCode, errorMessage(preview.error))}</AlertDescription>
          </Alert>
        ) : summary === null ? null : (
          <div className="grid gap-2" data-testid="plan-preview">
            <p className="font-medium">{summary.headline}</p>
            {summary.dueNow === null ? null : (
              <p className="text-2xl font-semibold tabular-nums" data-testid="due-now">
                {summary.dueNow} <span className="text-sm font-normal text-muted-foreground">due today</span>
              </p>
            )}
            <p className="text-sm text-muted-foreground">{summary.detail}</p>
          </div>
        )}

        {change.isError ? (
          <Alert variant="destructive" data-testid="change-error">
            <AlertDescription>{planErrorText(changeCode, errorMessage(change.error))}</AlertDescription>
          </Alert>
        ) : null}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={change.isPending}>
            {result === undefined ? 'Cancel' : 'Close'}
          </Button>
          {result !== undefined ? null : (
            <Button
              data-testid="confirm-change"
              disabled={summary === null || change.isPending}
              onClick={() => {
                change.mutate({ plan, interval })
              }}
            >
              {change.isPending
                ? 'Working…'
                : summary?.immediate === true
                  ? `Confirm and pay ${summary.dueNow ?? ''}`.trim()
                  : keeping
                    ? 'Keep my plan'
                    : 'Confirm'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
